// store/modules/cart.js
import axios from '@/api/axios';

const state = {
  cartCount: 0,
  cartItems: [],
  cartSummary: {
    total_items: 0,
    total_quantity: 0,
    subtotal: 0,
    total: 0,
  },
  loading: false,
};

const mutations = {
  SET_CART_COUNT(state, count) {
    state.cartCount = count;
  },
  SET_CART_ITEMS(state, items) {
    state.cartItems = items;
  },
  SET_CART_SUMMARY(state, summary) {
    state.cartSummary = summary;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  ADD_CART_ITEM(state, item) {
    const existingIndex = state.cartItems.findIndex(i => i.id === item.id);
    if (existingIndex !== -1) {
      state.cartItems[existingIndex] = item;
    } else {
      state.cartItems.push(item);
    }
  },
  UPDATE_CART_ITEM(state, updatedItem) {
    const index = state.cartItems.findIndex(i => i.id === updatedItem.id);
    if (index !== -1) {
      state.cartItems.splice(index, 1, updatedItem);
    }
  },
  REMOVE_CART_ITEM(state, itemId) {
    state.cartItems = state.cartItems.filter(i => i.id !== itemId);
  },
  CLEAR_CART(state) {
    state.cartItems = [];
    state.cartCount = 0;
    state.cartSummary = {
      total_items: 0,
      total_quantity: 0,
      subtotal: 0,
      total: 0,
    };
  },
};

const actions = {
  // Fetch cart from API
  async fetchCart({ commit }) {
    commit('SET_LOADING', true);
    try {
      const response = await axios.get('/cart');
      if (response.data.success) {
        commit('SET_CART_ITEMS', response.data.data.items || []);
        commit('SET_CART_SUMMARY', response.data.data.summary || {});
        commit('SET_CART_COUNT', response.data.data.summary?.total_quantity || 0);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get cart count
  async fetchCartCount({ commit }) {
    try {
      const response = await axios.get('/cart/count');
      if (response.data.success) {
        commit('SET_CART_COUNT', response.data.data.count || 0);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to fetch cart count:', error);
      commit('SET_CART_COUNT', 0);
      throw error;
    }
  },

  // Add item to cart
  async addToCart({ commit, dispatch }, payload) {
    try {
      const response = await axios.post('/cart', payload);
      if (response.data.success) {
        commit('ADD_CART_ITEM', response.data.data);
        await dispatch('fetchCartCount');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  },

  // Update cart item quantity
  async updateCartItem({ commit, dispatch }, { itemId, quantity }) {
    try {
      const response = await axios.put(`/cart/${itemId}`, { quantity });
      if (response.data.success) {
        commit('UPDATE_CART_ITEM', response.data.data);
        await dispatch('fetchCartCount');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update cart item:', error);
      throw error;
    }
  },

  // Remove item from cart
  async removeCartItem({ commit, dispatch }, itemId) {
    try {
      const response = await axios.delete(`/cart/${itemId}`);
      if (response.data.success) {
        commit('REMOVE_CART_ITEM', itemId);
        await dispatch('fetchCartCount');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to remove cart item:', error);
      throw error;
    }
  },

  // Clear entire cart
  async clearCart({ commit }) {
    try {
      const response = await axios.delete('/cart');
      if (response.data.success) {
        commit('CLEAR_CART');
      }
      return response.data;
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  },

  // Set cart count manually (for logout, etc.)
  setCartCount({ commit }, count) {
    commit('SET_CART_COUNT', count);
  },
};

const getters = {
  cartCount: state => state.cartCount,
  cartItems: state => state.cartItems,
  cartSummary: state => state.cartSummary,
  cartLoading: state => state.loading,
  cartTotal: state => state.cartSummary.total,
  cartItemsCount: state => state.cartItems.length,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};